import { useEffect, useMemo, useState } from "react";
import { Euro, FolderKanban, Search, TrendingDown, TrendingUp } from "lucide-react";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";

const STATUS_META = {
  gezond: {
    label: "Op koers",
    pill: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  warning: {
    label: "Let op",
    pill: "border-amber-200 bg-amber-50 text-amber-700",
  },
  risk: {
    label: "Overschrijding",
    pill: "border-rose-200 bg-rose-50 text-rose-700",
  },
  unknown: {
    label: "Nog geen budget",
    pill: "border-slate-200 bg-slate-50 text-slate-700",
  },
};

export default function ProjectControlPage() {
  const { user } = useUser();
  const [projectControlRows, setProjectControlRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [controllerFocus, setControllerFocus] = useState("all");

  const roleNames = useMemo(
    () =>
      new Set(
        [
          user?.rol,
          user?.role,
          ...(Array.isArray(user?.roles)
            ? user.roles.map((role) => (typeof role === "string" ? role : role?.name || role?.naam))
            : []),
        ]
          .filter(Boolean)
          .map((value) => String(value).trim().toLowerCase())
      ),
    [user]
  );
  const isControllerOnly =
    roleNames.has("controller")
    && !roleNames.has("projectleider")
    && !roleNames.has("tenantadmin")
    && !roleNames.has("platformadmin");

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);
        setError("");
        const summaryRes = await api.get("/projects/control-summary");

        if (!mounted) return;
        setProjectControlRows(Array.isArray(summaryRes.data) ? summaryRes.data : []);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Projectcontrol laden mislukt");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  const normalizedQuery = query.trim().toLowerCase();

  const rows = useMemo(() => {
    return projectControlRows
      .map((row) => {
        const budgetHours = Number(row.budgetHours.toFixed(1));
        const budgetRevenueExcl = Number(row.budgetRevenueExcl.toFixed(2));
        const budgetMaterialsExcl = Number(row.budgetMaterialsExcl.toFixed(2));
        const hasBudget = budgetHours > 0;
        const registeredHours = Number(row.registeredHours.toFixed(1));
        const laborCostExcl = Number((row.laborCostExcl || 0).toFixed(2));
        const laborRevenueExcl = Number((row.laborRevenueExcl || 0).toFixed(2));
        const laborMarginExcl = Number((row.laborMarginExcl || 0).toFixed(2));
        const materialRevenueExcl = Number((row.materialRevenueExcl || 0).toFixed(2));
        const realizedRevenueExcl = Number((row.realizedRevenueExcl || 0).toFixed(2));
        const budgetUsage = budgetHours > 0 ? registeredHours / budgetHours : 0;
        const revenueUsage = budgetRevenueExcl > 0 ? realizedRevenueExcl / budgetRevenueExcl : 0;
        const materialUsage = budgetMaterialsExcl > 0 ? materialRevenueExcl / budgetMaterialsExcl : 0;
        const status = getProjectControlStatus(
          hasBudget || budgetRevenueExcl > 0 || budgetMaterialsExcl > 0,
          Math.max(budgetUsage, revenueUsage, materialUsage)
        );
        return {
          ...row,
          hasBudget,
          budgetHours,
          budgetRevenueExcl,
          budgetMaterialsExcl,
          registeredHours,
          laborCostExcl,
          laborRevenueExcl,
          laborMarginExcl,
          materialRevenueExcl,
          realizedRevenueExcl,
          budgetUsage,
          revenueUsage,
          materialUsage,
          status,
        };
      })
      .filter((row) => {
        if (statusFilter !== "all" && row.status !== statusFilter) return false;
        if (
          isControllerOnly
          && controllerFocus === "attention"
          && !["warning", "risk", "unknown"].includes(row.status)
        ) {
          return false;
        }
        if (!normalizedQuery) return true;
        return [row.projectNummer, row.projectNaam, row.clientName, row.locationText]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        const severity = { risk: 0, warning: 1, gezond: 2, unknown: 3 };
        if (severity[a.status] !== severity[b.status]) return severity[a.status] - severity[b.status];
        return b.realizedRevenueExcl - a.realizedRevenueExcl;
      });
  }, [controllerFocus, isControllerOnly, normalizedQuery, projectControlRows, statusFilter]);

  const totals = useMemo(() => {
    return {
      projects: rows.length,
      revenueExcl: Number(rows.reduce((sum, row) => sum + row.realizedRevenueExcl, 0).toFixed(2)),
      laborCostExcl: Number(rows.reduce((sum, row) => sum + row.laborCostExcl, 0).toFixed(2)),
      laborMarginExcl: Number(rows.reduce((sum, row) => sum + row.laborMarginExcl, 0).toFixed(2)),
      risk: rows.filter((row) => row.status === "risk").length,
    };
  }, [rows]);

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.45),_transparent_32%),linear-gradient(135deg,_#0f172a,_#1e293b_55%,_#334155)] p-6 text-white shadow-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-200">
              Projectcontrol
            </p>
            <h2 className="text-3xl font-semibold tracking-tight">
              Financieel beeld per project op basis van tijdregels en snapshots.
            </h2>
            <p className="max-w-2xl text-sm text-slate-200">
              Uren, kostprijs, omzet en marge van arbeid komen uit `TimeEntry` snapshots. Materiaalomzet komt uit werkbon-totalen.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:min-w-[420px] lg:grid-cols-4">
            <MetricCard label="Projecten" value={totals.projects} icon={FolderKanban} />
            <MetricCard label="Omzet excl" value={`€ ${toMoney(totals.revenueExcl)}`} icon={TrendingUp} />
            <MetricCard label="Arbeid kost" value={`€ ${toMoney(totals.laborCostExcl)}`} icon={Euro} />
            <MetricCard label="Overschrijding" value={totals.risk} icon={TrendingDown} />
          </div>
        </div>
      </div>

      <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <label className="block max-w-md">
            <span className="mb-1 block text-xs font-medium text-slate-600">Zoeken</span>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Project, klant, locatie..."
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
              />
            </div>
          </label>

          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "Alles" },
              { key: "gezond", label: "Op koers" },
              { key: "warning", label: "Let op" },
              { key: "risk", label: "Overschrijding" },
              { key: "unknown", label: "Geen budget" },
            ].map((option) => {
              const active = statusFilter === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setStatusFilter(option.key)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                    active
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {isControllerOnly && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Controller filter</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "Alles tonen" },
                { key: "attention", label: "Alleen aandacht nodig" },
              ].map((option) => {
                const active = controllerFocus === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setControllerFocus(option.key)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {loading && (
        <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          Projectcontrol laden...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <section className="space-y-4">
          {rows.map((row) => {
            const meta = STATUS_META[row.status] || STATUS_META.unknown;
            return (
              <article key={row.projectId} className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 bg-slate-50/80 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                      {row.projectNummer}
                    </p>
                    <h3 className="text-lg font-semibold text-slate-950">{row.projectNaam}</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      {row.clientName} · {row.locationText}
                    </p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${meta.pill}`}>
                    {meta.label}
                  </span>
                </div>

                <div className="grid gap-4 p-5 lg:grid-cols-4 xl:grid-cols-8">
                  <ValueCard label="Begrote uren" value={row.hasBudget ? `${row.budgetHours.toFixed(1)} u` : "-"} />
                  <ValueCard label="Geregistreerde uren" value={`${row.registeredHours.toFixed(1)} u`} />
                  <ValueCard label="Arbeid kost excl" value={`€ ${toMoney(row.laborCostExcl)}`} />
                  <ValueCard label="Arbeid omzet excl" value={`€ ${toMoney(row.laborRevenueExcl)}`} />
                  <ValueCard label="Arbeid marge excl" value={`€ ${toMoney(row.laborMarginExcl)}`} />
                  <ValueCard label="Materiaal omzet excl" value={`€ ${toMoney(row.materialRevenueExcl)}`} />
                  <ValueCard
                    label="Begrote omzet excl"
                    value={row.budgetRevenueExcl > 0 ? `€ ${toMoney(row.budgetRevenueExcl)}` : "-"}
                  />
                  <ValueCard label="Totale omzet excl" value={`€ ${toMoney(row.realizedRevenueExcl)}`} />
                </div>

                <div className="px-5 pb-5">
                  <div className="grid gap-4 lg:grid-cols-3">
                    <BudgetProgressCard
                      label="Gebruik urenbudget"
                      usage={row.budgetUsage}
                      enabled={row.hasBudget}
                      status={row.status}
                      helpText="Gebaseerd op project.budgetHours en TimeEntry minutes."
                    />
                    <BudgetProgressCard
                      label="Gebruik omzetbudget"
                      usage={row.revenueUsage}
                      enabled={row.budgetRevenueExcl > 0}
                      status={row.status}
                      helpText="Gebaseerd op project.budgetRevenueExcl en arbeid+materiaal omzet."
                    />
                    <BudgetProgressCard
                      label="Gebruik materiaalbudget"
                      usage={row.materialUsage}
                      enabled={row.budgetMaterialsExcl > 0}
                      status={row.status}
                      helpText="Gebaseerd op project.budgetMaterialsExcl en financialTotals.materialsSubtotal."
                    />
                  </div>
                </div>
              </article>
            );
          })}

          {rows.length === 0 && (
            <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
              Geen projecten gevonden voor deze filter.
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function MetricCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.22em] text-slate-200">{label}</span>
        <Icon size={16} className="text-amber-200" />
      </div>
      <p className="mt-3 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function ValueCard({ label, value }) {
  return (
    <div className="rounded-[18px] border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function BudgetProgressCard({ label, usage, enabled, status, helpText }) {
  return (
    <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between text-sm font-medium text-slate-700">
        <span>{label}</span>
        <span>{enabled ? `${Math.round(usage * 100)}%` : "Geen budget"}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${
            status === "risk"
              ? "bg-rose-500"
              : status === "warning"
                ? "bg-amber-500"
                : status === "gezond"
                  ? "bg-emerald-500"
                  : "bg-slate-400"
          }`}
          style={{ width: `${enabled ? Math.max(4, Math.min(usage * 100, 100)) : 10}%` }}
        />
      </div>
      <p className="mt-3 text-sm text-slate-600">{helpText}</p>
    </div>
  );
}

function getProjectControlStatus(hasBudget, usage) {
  if (!hasBudget) return "unknown";
  if (usage > 1) return "risk";
  if (usage >= 0.85) return "warning";
  return "gezond";
}

function toMoney(value) {
  const numeric = Number(value || 0);
  return numeric.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
